import type { LanguageTenses, Tense } from './tenses';

// ─────────────────────────────────────────────────────────────────────────────
// English: every tense. All five forms of each verb are written out (base,
// -s, -ing, past, past participle) rather than generated — English spelling
// (stopping, studying, swum, forgotten) is too irregular for rules to be safe.
// ─────────────────────────────────────────────────────────────────────────────

type V = [base: string, s: string, ing: string, past: string, pp: string];
const V: V[] = [
  ['work', 'works', 'working', 'worked', 'worked'],
  ['play', 'plays', 'playing', 'played', 'played'],
  ['study', 'studies', 'studying', 'studied', 'studied'],
  ['watch', 'watches', 'watching', 'watched', 'watched'],
  ['live', 'lives', 'living', 'lived', 'lived'],
  ['stop', 'stops', 'stopping', 'stopped', 'stopped'],
  ['travel', 'travels', 'traveling', 'traveled', 'traveled'],
  ['go', 'goes', 'going', 'went', 'gone'],
  ['eat', 'eats', 'eating', 'ate', 'eaten'],
  ['see', 'sees', 'seeing', 'saw', 'seen'],
  ['write', 'writes', 'writing', 'wrote', 'written'],
  ['take', 'takes', 'taking', 'took', 'taken'],
  ['make', 'makes', 'making', 'made', 'made'],
  ['have', 'has', 'having', 'had', 'had'],
  ['do', 'does', 'doing', 'did', 'done'],
  ['run', 'runs', 'running', 'ran', 'run'],
  ['swim', 'swims', 'swimming', 'swam', 'swum'],
  ['read', 'reads', 'reading', 'read', 'read'],
  ['buy', 'buys', 'buying', 'bought', 'bought'],
  ['speak', 'speaks', 'speaking', 'spoke', 'spoken'],
  ['drink', 'drinks', 'drinking', 'drank', 'drunk'],
  ['sleep', 'sleeps', 'sleeping', 'slept', 'slept'],
  ['come', 'comes', 'coming', 'came', 'come'],
  ['begin', 'begins', 'beginning', 'began', 'begun'],
  ['forget', 'forgets', 'forgetting', 'forgot', 'forgotten'],
];
const BY = Object.fromEntries(V.map(v => [v[0], v]));
// No meaning hints: an English class's students may speak any language.
const verbs = Object.fromEntries(V.map(v => [v[0], '']));

// People: I, you, he/she, we, they.
const BE_PRES = ['am', 'are', 'is', 'are', 'are'];
const BE_PAST = ['was', 'were', 'was', 'were', 'were'];
const HAVE = ['have', 'have', 'has', 'have', 'have'];
const third = (i: number) => i === 2;

/** The US spelling is shown; the British double l (travelled, travelling) is accepted too. */
function withUk(s: string): string[] {
  const uk = s.replace(/\btravel(?=(ed|ing)\b)/, 'travell');
  return uk === s ? [s] : [s, uk];
}

const f = (fn: (v: V, i: number) => string, after: string, id: string, title: string, blurb: string): Tense => ({
  id, title, blurb, verbs, after, forms: (verb, i) => withUk(fn(BY[verb], i)),
});

const TENSES: Tense[] = [
  f((v, i) => (third(i) ? v[1] : v[0]), 'every day.', 'present_simple', 'Present simple', 'I work, she works…'),
  f((v, i) => `${BE_PRES[i]} ${v[2]}`, 'right now.', 'present_continuous', 'Present continuous', 'I am working…'),
  f(v => v[3], 'yesterday.', 'past_simple', 'Past simple', 'worked, went, ate…'),
  f((v, i) => `${BE_PAST[i]} ${v[2]}`, 'at 8 pm last night.', 'past_continuous', 'Past continuous', 'I was working…'),
  f(v => `will ${v[0]}`, 'tomorrow.', 'future_will', 'Future: will', 'I will work…'),
  f((v, i) => `${BE_PRES[i]} going to ${v[0]}`, 'next week.', 'future_going_to', 'Future: going to', 'I am going to work…'),
  f(v => `will be ${v[2]}`, 'at this time tomorrow.', 'future_continuous', 'Future continuous', 'I will be working…'),
  f((v, i) => `${HAVE[i]} ${v[4]}`, 'many times.', 'present_perfect', 'Present perfect', 'I have worked, she has gone…'),
  f((v, i) => `${HAVE[i]} been ${v[2]}`, 'for two hours.', 'present_perfect_continuous', 'Present perfect continuous', 'I have been working…'),
  f(v => `had ${v[4]}`, 'before class started.', 'past_perfect', 'Past perfect', 'I had worked…'),
  f(v => `had been ${v[2]}`, 'for an hour when you called.', 'past_perfect_continuous', 'Past perfect continuous', 'I had been working…'),
  f(v => `will have ${v[4]}`, 'by next Friday.', 'future_perfect', 'Future perfect', 'I will have worked…'),
  f(v => `will have been ${v[2]}`, 'for ten years by 2030.', 'future_perfect_continuous', 'Future perfect continuous', 'I will have been working…'),
  f(v => `would ${v[0]}`, 'if I had more time.', 'conditional', 'Conditional', 'I would work…'),
  f(v => `would be ${v[2]}`, 'if it weren’t raining.', 'conditional_continuous', 'Conditional continuous', 'I would be working…'),
  f(v => `would have ${v[4]}`, 'if I had known.', 'conditional_perfect', 'Conditional perfect', 'I would have worked…'),
];

export const EN_TENSES: LanguageTenses = {
  lang: 'en',
  people: [['I'], ['You'], ['He', 'She'], ['We'], ['They']],
  tenses: TENSES,
};
